import { useCallback, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
  Handle,
  Position,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type NodeChange,
  type EdgeChange,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Diagram } from './Diagram';

/**
 * Interactive operator-stack canvas.
 *
 * Used in Lesson 8.5 capstone, Stage 2 (Section 04: "Your starter stack").
 *
 * Students:
 *   1. Start with five tool-category nodes pre-placed (Chat, Voice, Research,
 *      Writing, Orchestration) and three job nodes (Drafting, Transcribing,
 *      Routing).
 *   2. Drag connections from each tool to the job it does.
 *   3. Click "Validate stack" to check the minimum-viable shape:
 *      at least 1-3 tool→job connections, no tool wired to more than 2 jobs,
 *      every wired tool has a different category.
 *   4. Use the result to write Section 04 of their operator brief.
 *
 * No data persistence: this is a thinking exercise, not a saved artifact.
 */

const TOOL_NODES: Node[] = [
  { id: 'tool-chat', type: 'tool', position: { x: 40, y: 30 }, data: { label: 'Chat', sub: 'first drafts, research' } },
  { id: 'tool-voice', type: 'tool', position: { x: 40, y: 110 }, data: { label: 'Voice', sub: 'transcribe, dictate' } },
  { id: 'tool-research', type: 'tool', position: { x: 40, y: 190 }, data: { label: 'Research', sub: 'sources, citations' } },
  { id: 'tool-writing', type: 'tool', position: { x: 40, y: 270 }, data: { label: 'Writing', sub: 'edit, sharpen' } },
  { id: 'tool-orchestration', type: 'tool', position: { x: 40, y: 350 }, data: { label: 'Orchestration', sub: 'route, automate' } },
];

const JOB_NODES: Node[] = [
  { id: 'job-draft', type: 'job', position: { x: 380, y: 60 }, data: { label: 'Drafting', sub: 'your essays, recaps, emails' } },
  { id: 'job-transcribe', type: 'job', position: { x: 380, y: 180 }, data: { label: 'Transcribing', sub: 'your interviews, voice notes' } },
  { id: 'job-route', type: 'job', position: { x: 380, y: 300 }, data: { label: 'Routing', sub: 'forms, files, follow-ups' } },
];

function ToolNode({ data }: NodeProps) {
  return (
    <div
      className="rounded-lg border border-amber/60 bg-amber/[0.08] px-3 py-2.5"
      style={{ minWidth: 150 }}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-amber">
        Tool category
      </div>
      <div className="mt-1 text-sm font-semibold text-text">{data.label}</div>
      <div className="mt-0.5 text-[12px] leading-snug text-text/75">{data.sub}</div>
      <Handle type="source" position={Position.Right} style={{ background: '#FFB547', width: 10, height: 10 }} />
    </div>
  );
}

function JobNode({ data }: NodeProps) {
  return (
    <div
      className="rounded-lg border border-emerald-400/60 bg-emerald-400/[0.08] px-3 py-2.5"
      style={{ minWidth: 170 }}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
        Job
      </div>
      <div className="mt-1 text-sm font-semibold text-text">{data.label}</div>
      <div className="mt-0.5 text-[12px] leading-snug text-text/75">{data.sub}</div>
      <Handle type="target" position={Position.Left} style={{ background: '#34D399', width: 10, height: 10 }} />
    </div>
  );
}

const NODE_TYPES = {
  tool: ToolNode,
  job: JobNode,
};

const INITIAL_NODES: Node[] = [...TOOL_NODES, ...JOB_NODES];

type ValidationResult = {
  status: 'ok' | 'warn' | 'empty';
  message: string;
};

function validate(nodes: Node[], edges: Edge[]): ValidationResult {
  if (edges.length === 0) {
    return {
      status: 'empty',
      message: 'No connections yet. Drag from a tool category on the left to a job on the right.',
    };
  }

  // Tools per job
  const toolsPerJob: Record<string, Set<string>> = {};
  for (const e of edges) {
    if (!toolsPerJob[e.target]) toolsPerJob[e.target] = new Set();
    toolsPerJob[e.target].add(e.source);
  }
  // Jobs per tool
  const jobsPerTool: Record<string, Set<string>> = {};
  for (const e of edges) {
    if (!jobsPerTool[e.source]) jobsPerTool[e.source] = new Set();
    jobsPerTool[e.source].add(e.target);
  }

  const overloadedTools = Object.entries(jobsPerTool).filter(
    ([, jobs]) => jobs.size > 2,
  );
  if (overloadedTools.length > 0) {
    const tool = nodes.find((n) => n.id === overloadedTools[0][0])?.data?.label ?? 'A tool';
    return {
      status: 'warn',
      message: `${tool} is doing too many jobs. Each tool earns its place by doing 1 to 2 jobs well. If it cannot, drop it from the stack.`,
    };
  }

  const totalTools = Object.keys(jobsPerTool).length;
  if (totalTools > 3) {
    return {
      status: 'warn',
      message: `You wired ${totalTools} tool categories. The brief says 1 to 3. More tools means more switching cost. Pick the three that actually matter to your week.`,
    };
  }

  const jobsWithNoTool = JOB_NODES.filter((j) => !toolsPerJob[j.id]);
  if (jobsWithNoTool.length > 0) {
    const job = jobsWithNoTool[0].data.label;
    return {
      status: 'warn',
      message: `${job} has no tool wired to it. If a job has no tool, write down how you do it without AI. That is a manual move worth naming.`,
    };
  }

  return {
    status: 'ok',
    message: `Stack looks honest: ${totalTools} tool categor${totalTools === 1 ? 'y' : 'ies'}, every job covered, no tool overloaded. Write Section 04 from this.`,
  };
}

export function OperatorStackCanvas() {
  const [nodes, setNodes] = useState<Node[]>(INITIAL_NODES);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [result, setResult] = useState<ValidationResult | null>(null);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );
  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            style: { stroke: '#FFB547', strokeWidth: 2 },
            animated: true,
          },
          eds,
        ),
      ),
    [],
  );

  const onValidate = () => setResult(validate(nodes, edges));
  const onReset = () => {
    setEdges([]);
    setResult(null);
  };

  const statusColor =
    result?.status === 'ok'
      ? 'border-emerald-400/50 bg-emerald-400/[0.05] text-text'
      : result?.status === 'warn'
        ? 'border-amber/50 bg-amber/[0.05] text-text'
        : 'border-line bg-surface text-muted';

  return (
    <Diagram
      figure="8.5"
      title="Map your operator stack"
      caption="Wire each tool category to the job it does. Then check the shape. Use the result to write Section 04 of your brief."
      ariaLabel="An interactive canvas with five tool-category nodes on the left (Chat, Voice, Research, Writing, Orchestration) and three job nodes on the right (Drafting, Transcribing, Routing). Students drag connections from tools to jobs, then click Validate stack to check the shape."
    >
      <div
        className="rounded-lg border border-line bg-ink"
        style={{ height: 460 }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={NODE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#26262E" gap={20} size={1} />
          <Controls
            className="!border !border-line !bg-surface"
            showInteractive={false}
          />
        </ReactFlow>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onValidate}
          className="rounded-md border border-amber bg-amber/[0.1] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-amber hover:bg-amber/[0.15]"
        >
          Validate stack
        </button>
        <button
          type="button"
          onClick={onReset}
          className="rounded-md border border-line bg-surface px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-muted hover:text-text"
        >
          Reset connections
        </button>
        <p className="ml-auto text-[11px] text-muted">
          Drag from the amber dot on a tool to the green dot on a job.
        </p>
      </div>

      {result && (
        <div className={`mt-4 rounded-lg border p-4 text-sm ${statusColor}`}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            {result.status === 'ok'
              ? 'Stack check: passed'
              : result.status === 'warn'
                ? 'Stack check: review'
                : 'Stack check: empty'}
          </p>
          <p className="mt-2 text-text/90">{result.message}</p>
        </div>
      )}
    </Diagram>
  );
}
